import React, { useContext, useMemo } from 'react';
import { Copy24Regular, Edit24Regular, Globe28Regular, LinkDismiss24Regular } from '@fluentui/react-icons';
import {
  Button,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Link as FluentLink,
} from '@fluentui/react-components';
import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import MenuContext from '@modules/documents/contexts/MenuContext.context';
import { ToastContext } from '@shared/contexts';
import { useIsSafari } from '@shared/hooks';
import useEditorActive from '../../../hooks/useEditorActive.hook';
import { ETiptapSelectorKey } from '../../../types/tiptap.types';
import NodeBubbleMenuShell from '../../../components/NodeBubbleMenuShell';
import { isLockedOutsideFields } from '../../extensions/TemplateLocking';
import { stripProtocol } from '@shared/utils';

const useStyles = makeStyles({
  link: {
    fontSize: tokens.fontSizeBase400,
    maxWidth: '210px',
    marginRight: tokens.spacingHorizontalS,
  },
  noBorder: {
    ...shorthands.borderColor('transparent'),
  },
});

const LinkNodeBubbleMenu = () => {
  const { editor, isEdit } = useContext(DocumentContext);
  const { open } = useContext(MenuContext);
  const { show } = useContext(ToastContext);
  const isSafari = useIsSafari(); // tooltips in a bubbleMenu do not work in Safari

  const href = useEditorActive((ed) => ed.getAttributes(ETiptapSelectorKey.LINK).href);

  const handleCopyClick = async () => {
    if (!href) return;
    try {
      await navigator.clipboard.writeText(href);
      show('info', 'Copied!');
    } catch {
      show('error', 'Failed to copy link.');
    }
  };
  const handleEditClick = () => {
    editor.chain().focus().extendMarkRange(ETiptapSelectorKey.LINK).run();
    setTimeout(() => open(href), 100);
  };
  const handleDeleteClick = () => {
    editor.chain().focus().extendMarkRange(ETiptapSelectorKey.LINK).unsetLink().run();
  };

  const classes = useStyles();

  // Copy stays enabled everywhere (clipboard-only); Edit/Remove edit the doc, which the locked
  // body of a bound doc silently filters.
  const locked = useEditorActive((ed) => isLockedOutsideFields(ed.state));
  const copyBtn = <Button icon={<Copy24Regular />} className={classes.noBorder} onClick={handleCopyClick} />;
  const editBtn = (
    <Button icon={<Edit24Regular />} className={classes.noBorder} disabled={locked} onClick={handleEditClick} />
  );
  const removeBtn = (
    <Button
      icon={<LinkDismiss24Regular />}
      className={classes.noBorder}
      disabled={locked}
      onClick={handleDeleteClick}
    />
  );

  const header = (
    <FluentLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={mergeClasses(classes.link, '!truncate', 'external-link')}
    >
      {href ? stripProtocol(href) : ''}
    </FluentLink>
  );

  const buttons = useMemo(
    () => [
      { content: copyBtn, tooltip: 'Copy Link' },
      { content: editBtn, tooltip: 'Edit Link' },
      { content: removeBtn, tooltip: 'Remove Link' },
    ],
    [copyBtn, editBtn, removeBtn]
  );

  return (
    <NodeBubbleMenuShell
      nodeName={ETiptapSelectorKey.LINK}
      editor={editor}
      isEdit={isEdit}
      extraPredicate={({ from, to }) => from === to}
      leadingIcon={<Globe28Regular />}
      header={header}
    >
      {buttons.map(({ content, tooltip }) =>
        isSafari ? (
          <React.Fragment key={tooltip}>{content}</React.Fragment>
        ) : (
          <Tooltip key={tooltip} content={tooltip} relationship="label" withArrow>
            {content}
          </Tooltip>
        )
      )}
    </NodeBubbleMenuShell>
  );
};

export default LinkNodeBubbleMenu;
